import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as dayjs from 'dayjs';
import {
  MenuCategory,
  MenuItem,
  MenuItemBulletList,
  MenuItemCatalog,
  MenuSection,
  RichDataModel,
} from 'src/app/models/rich-data.model';
import { environment } from 'src/environments/environment';
import { BasicInfo } from '../models/basic-info.model';
import { MenuItemBookState } from '../models/menu-item-book-state';
import { MenuTagModel } from '../models/menu-tag.model';
import { RegisterCode } from '../models/register-code.model';
import { ToastMachineService } from './toast-machine.service';
import { AnalyticsService } from './analytics.service';
import { Router } from '@angular/router';
import { ActionButtonConfig } from '../models/action-button-config.model';
import { AuthService } from './auth.service';
import { HotelListModel } from '../models/hotel-list.model';
@Injectable({
  providedIn: 'root',
})
export class DataService {
  baseData: RichDataModel = JSON.parse(localStorage.getItem('rData'));
  rData: RichDataModel = JSON.parse(localStorage.getItem('rData'));

  refresh = false;
  requestActive = true;

  lastFeatured: string = null;

  menuItem: MenuItem = null;
  menuCategory: MenuCategory = null;
  menuSection: MenuSection = null;

  menuSections: MenuSection[] = [];
  sectionsTitle: string = null;

  liveRequests: { icon: any; data: MenuSection }[] = [];
  homePage: HomePageItem[] = [];

  quickActionConfig: ActionButtonConfig[] = [];
  bottomButtonConfig: ActionButtonConfig[] = [];
  appHotelsTags: MenuTagModel[] = [];
  constructor(
    private http: HttpClient,
    private toaster: ToastMachineService,
    private translateServ: TranslateService,
    private analyticService: AnalyticsService,
    private injector: Injector,
    private router: Router
  ) {}

  async init(): Promise<void> {
    if (!this.rData) {
      if (!environment.hotelId) {
        const SUBDOMAIN = window.location.hostname.split('.')[0];
        try {
          await this.getLinkwithSub(SUBDOMAIN);
        } catch (error) {
          this.router.navigate(['/hotels'], {
            queryParams: { refresh: Date.now() },
          });
          return;
        }
      }

      this.rData = await this.getRichData(environment.hotelId);
    } else {
      if (this.rData.id !== environment.hotelId) {
        this.rData = await this.getRichData(environment.hotelId);
      } else {
        await this.versionControl();
      }
    }

    environment.hotelId = this.rData.id;

    this.quickActionConfig = this.rData.action_buttons?.filter(
      (x) => x.type === 'quick_action'
    );
    this.bottomButtonConfig = this.rData.action_buttons?.filter(
      (x) => x.type === 'bottom_menu'
    );

    const auth = this.injector.get(AuthService);
    // this.router.navigate(['/home']);

    this.analyticService.init(this.rData.google_analytics_link);
  }

  getRichData(hotelId: number = environment.hotelId): Promise<RichDataModel> {
    if (!hotelId) {
      throw new Error('Hotel ID is required!');
    }

    return new Promise((resolve) => {
      this.http
        .get(
          environment.awsURL +
            (hotelId ||
              +localStorage.getItem('hotelId') ||
              environment.hotelId) +
            '/RichData.gz?' +
            new Date().getTime()
        )
        .subscribe({
          next: async (resp: RichDataModel) => {
            try {
              localStorage.setItem('rData', JSON.stringify(resp));
            } catch {
              console.error('LocalStorage memory limit reached!');
            }

            this.baseData = resp;
            this.rData = resp;
            this.setColor();
            this.setFonts();
            this.setLanguage();
            resp = await this.removeDateRange(resp);
            resolve(resp);
          },
          error: async () => {
            if (
              environment.hotelId ||
              localStorage.getItem('hotelId') ||
              hotelId
            ) {
              const RESP = await this.getRichJson(hotelId);
              this.removeDateRange(RESP);
              resolve(RESP);
            }
            this.setColor();
          },
        });
    });
  }

  getMobileGzip(hotelId?: number): Promise<RichDataModel> {
    return new Promise((resolve, reject) => {
      this.http
        .get(
          environment.awsURL +
            (hotelId ||
              +localStorage.getItem('hotelId') ||
              environment.hotelId) +
            '/MobileData.gz?' +
            new Date().getTime()
        )
        .subscribe({
          next: (resp: RichDataModel) => {
            resolve(resp);
          },
          error: (err) => {
            const error =
              err.error && err.error.message ? err.error.message : err.message;
            this.toaster.toastMachine(error, 'danger', 3000);
            reject(err);
          },
        });
    });
  }

  getRichJson(hotelId?: number): Promise<RichDataModel> {
    return new Promise((resolve, reject) => {
      this.http
        .get(
          environment.awsURL +
            (hotelId ||
              +localStorage.getItem('hotelId') ||
              environment.hotelId) +
            '/RichMobileData.json?' +
            new Date().getTime()
        )
        .subscribe({
          next: (resp: RichDataModel) => {
            try {
              localStorage.setItem('rData', JSON.stringify(resp));
            } catch {
              console.error('LocalStorage memory limit reached!');
            }

            this.baseData = resp;
            resolve(resp);
          },
          error: (err) => {
            const error =
              err.error && err.error.message ? err.error.message : err.message;
            this.toaster.toastMachine(error, 'danger', 3000);
            reject(err);
          },
        });
    });
  }

  registerCode(registerCode: string): Promise<RegisterCode> {
    return new Promise((resolve, reject) => {
      this.http
        .get(
          environment.URL +
            '/exapi/reservation_from_register_code?registerCode=' +
            registerCode
        )
        .subscribe({
          next: (resp: any) => {
            resolve(resp);
          },
          error: (err) => {
            reject(err);
          },
        });
    });
  }

  getMenuItemBookState(menuItemId: number): Promise<MenuItemBookState> {
    return new Promise((resolve, reject) => {
      this.http
        .get(
          environment.URL +
            '/exapi/menu_item_book_state_with_details/' +
            menuItemId +
            '?locale=' +
            this.translateServ.defaultLang
        )
        .subscribe({
          next: (resp: any) => {
            resolve(resp);
          },
          error: (err) => {
            reject(err);
          },
        });
    });
  }

  removeDateRange(data: RichDataModel): Promise<RichDataModel> {
    return new Promise(async (resolve) => {
      if (!this.baseData) {
        await this.getRichData();
      }

      this.rData = JSON.parse(JSON.stringify(this.baseData));

      this.requestActive =
        this.baseData.open_for_year ||
        (dayjs(new Date()).diff(this.baseData.open_date) > 0 &&
          dayjs(new Date()).diff(this.baseData.close_date) < 0);

      if (this.baseData.close_request) {
        this.requestActive = false;
      }
      for (const key in data) {
        if (
          data[key] instanceof Object &&
          data[key].show &&
          data[key].menu_categories
        ) {
          for (const [index, cat] of (
            data[key].menu_categories as MenuCategory[]
          ).entries()) {
            if (cat.menu_items) {
              for (const [itemIndex, item] of cat.menu_items.entries()) {
                if (item.show_date_range) {
                  if (
                    dayjs(new Date()).diff(dayjs(new Date(item.start_date))) <
                      0 ||
                    dayjs(new Date()).diff(dayjs(new Date(item.end_date))) > 0
                  ) {
                    cat.menu_items.splice(itemIndex, 1);
                  }
                }

                if (
                  item.tags &&
                  item.tags.find((tag) => tag.name === 'display-none')
                ) {
                  item.active = false;
                }
              }
            }

            if (!cat.menu_items || cat.menu_items.length === 0) {
              data[key].menu_categories.splice(index, 1);
            }
          }
        }
      }

      if (data.group_hotels && data.group_hotels.split(',').length === 1) {
        data.group_hotels = null;
      }

      resolve(data);
    });
  }

  versionControl() {
    return new Promise(async (resolve) => {
      if (this.rData) {
        const PATH =
          environment.awsURL +
          environment.hotelId +
          '/MobileVersion.json?' +
          new Date().getTime();

        this.http.get(PATH).subscribe(async (resp: { version: number }) => {
          if (this.rData.mobile_version !== resp.version) {
            await this.getRichData();
            this.rData = await this.removeDateRange(this.baseData);

            resolve(true);
          }

          resolve(false);
        });

        this.setColor();
        this.setLanguage();
        this.removeDateRange(this.baseData);
      }
    });
  }

  setLanguage() {
    const LANG =
      localStorage.getItem('userLang') ||
      navigator.language.substring(0, 2).toLowerCase();
    if (!this.rData?.languages.includes(LANG)) {
      localStorage.setItem('userLang', 'en');
      this.translateServ.setDefaultLang('en');
    } else {
      this.translateServ.setDefaultLang(LANG);
    }
  }

  setFonts() {
    if (this.rData) {
      const font1 = document.createElement('link');
      font1.href = this.rData.font_1_link;
      font1.rel = 'stylesheet';
      document.head.appendChild(font1);

      const font2 = document.createElement('link');
      font2.href = this.rData.font_2_link;
      font2.rel = 'stylesheet';
      document.head.appendChild(font2);

      const style = document.createElement('style');
      style.innerHTML = `
        @font-face {
            font-family: 'Tiny5';
            src: url('https://fonts.googleapis.com/css2?family=Tiny5&display=swap') format('woff');
            font-weight: normal;
            font-style: normal;
        }
    `;
      document.head.appendChild(style);

      document.documentElement.style.setProperty(
        '--font-1',
        this.rData.font_1_name
      );
      document.documentElement.style.setProperty(
        '--font-2',
        this.rData.font_2_name
      );
    }
  }

  setColor() {
    if (this.rData) {
      const hexToRgb = (hex: string): string => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result
          ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(
              result[3],
              16
            )} `
          : null;
      };

      document.documentElement.style.setProperty(
        '--ion-color-primary',
        this.rData.master_color
      );
      document.documentElement.style.setProperty(
        '--ion-color-primary-rgb',
        hexToRgb(this.rData.master_color)
      );
      document.documentElement.style.setProperty(
        '--ion-color-primary-shade',
        this.rData.master_color
      );
      document.documentElement.style.setProperty(
        '--ion-color-primary-tint',
        this.rData.master_color
      );
      document.documentElement.style.setProperty(
        '--ion-color-primary-light',
        this.rData.master_color + '25'
      );

      document.documentElement.style.setProperty(
        '--ion-color-secondary',
        this.rData.sub_color
      );
      document.documentElement.style.setProperty(
        '--ion-color-secondary-rgb',
        hexToRgb(this.rData.sub_color)
      );
      document.documentElement.style.setProperty(
        '--ion-color-secondary-shade',
        this.rData.sub_color
      );
      document.documentElement.style.setProperty(
        '--ion-color-secondary-tint',
        this.rData.sub_color
      );
    }
  }

  getBasicInfo(subdomain: string): Promise<BasicInfo> {
    return new Promise((resolve) => {
      this.http
        .get(environment.baseURL + '/getHotelType/' + subdomain)
        .subscribe({
          next: (resp: BasicInfo) => {
            resolve(resp);
          },
          error: (err) => {
            resolve(err);
          },
        });
    });
  }

  async getSection(sectionId: number): Promise<void> {
    if (!this.rData || this.rData.id !== environment.hotelId) {
      await this.getRichData();
      this.removeDateRange(this.baseData);
    }

    for (const key in this.rData) {
      if (this.rData[key] && this.rData[key].show) {
        if (this.rData[key].id === sectionId) {
          this.menuSection = this.rData[key];
          this.menuSections.push(this.menuSection);
          break;
        }
      }
    }

    return null;
  }

  getItem(itemId: number): void {
    if (this.menuSection && this.menuSection.menu_categories) {
      for (const cat of this.menuSection.menu_categories) {
        if (cat.menu_items) {
          for (const item of cat.menu_items) {
            if (item.id === itemId) {
              this.menuCategory = cat;
              this.menuItem = item;
              break;
            }
          }
        }
      }
    }

    return null;
  }

  async categorization() {
    if (!this.rData) {
      await this.getRichData();
    }

    const SECS = [
      { icon: 'assets/svg/platter.svg', key: 'room_services' },
      { icon: 'assets/svg/shampoo.svg', key: 'amenities' },
      { icon: 'assets/svg/housekeeping.svg', key: 'housekeeping' },
      { icon: 'assets/svg/issue.svg', key: 'issues' },
      { icon: 'assets/svg/relation.svg', key: 'complaint' },
      { icon: 'assets/svg/reception.svg', key: 'front_office' },
      { icon: 'assets/svg/profile-icon.svg', key: 'guest_relations' },
    ];

    this.liveRequests = [];
    this.homePage = [];

    SECS.forEach((sec) => {
      if (
        this.rData &&
        this.rData[sec.key] &&
        this.rData[sec.key].show &&
        this.rData[sec.key].menu_categories &&
        this.rData[sec.key].menu_categories.length > 0 &&
        this.rData[sec.key].menu_categories[0].menu_items &&
        this.rData[sec.key].menu_categories[0].menu_items.length > 0
      ) {
        this.liveRequests.push({ icon: sec.icon, data: this.rData[sec.key] });
      }
    });

    const ITEMS = [
      { data: [this.rData.other_services] },
      { data: [this.rData.news] },
      { data: [this.rData.restaurants, this.rData.bars, this.rData.cafes] },
      {
        data: [
          this.rData.activity,
          this.rData.daily_animations,
          this.rData.shows,
          this.rData.sport,
          this.rData.kids_club,
        ],
      },
      { data: [this.rData.opportunities] },
      { data: [this.rData.rooms] },
      { data: [this.rData.spa] },
      { data: [this.rData.pools] },
      { data: [this.rData.meeting_rooms] },
      { data: [this.rData.events] },
      { data: [this.rData.tours] },
      { data: [this.rData.sport] },
      { data: [this.rData.malls] },
      { data: [this.rData.wedding] },
      { data: [this.rData.po_is] },
      { data: [this.rData.golf_club] },
      { data: [this.rData.room_guide] },
      { data: [this.rData.upsell] },
      { data: [this.rData.mice] },
      { data: [this.rData.avm] },
      { data: [this.rData.life] },
      { data: [this.rData.cinema] },
      { data: [this.rData.news] },
      { data: [this.rData.sustainability] },
      // { data: [this.rData.banner] },
    ];

    ITEMS.forEach((content) => {
      if (
        (content.data[0] &&
          content.data[0] &&
          content.data[0].show &&
          content.data[0].menu_categories &&
          content.data[0].menu_categories.length > 0 &&
          content.data[0].menu_categories[0].menu_items &&
          content.data[0].menu_categories[0].menu_items.length > 0) ||
        content.data.length > 1
      ) {
        this.homePage.push({
          title:
            content.data.length > 1
              ? content.data[0].title
              : content.data[0].title,
          show_only_logged: content.data[0].show_only_logged,
          content:
            content.data.length > 1
              ? content.data.map((section) => {
                  if (
                    section &&
                    section.show &&
                    section.menu_categories &&
                    section.menu_categories.length > 0 &&
                    section.menu_categories[0].menu_items &&
                    section.menu_categories[0].menu_items.length > 0
                  ) {
                    return {
                      title: section.title,
                      image:
                        section.menu_categories[0].images &&
                        section.menu_categories[0].images.length > 0
                          ? section.menu_categories[0].images[0].url
                          : section.menu_categories[0].menu_items[0].image_url,
                      route: `/home/${
                        section.menu_area_id === 40
                          ? `e/${section.id}`
                          : section.id
                      }`,
                      type: 'section',
                      section,
                      item: null,
                    };
                  } else {
                    return null;
                  }
                })
              : [].concat(
                  ...content.data[0].menu_categories?.map((category) =>
                    category.menu_items?.map((item) => {
                      const DATA = {} as HomePageItemContent;
                      DATA.route = `/home/${item.menu_section_id}/${item.id}`;
                      DATA.title = item.name;
                      DATA.image = item.image_url;
                      DATA.type = 'item';
                      DATA.section = content.data[0];
                      DATA.item = item;
                      return DATA;
                    })
                  )
                ),
        });
      }
    });

    this.homePage = this.homePage.filter(
      (item) => item && item.content && item.content.length > 0
    );
    this.homePage.forEach((home) => {
      home.content = home.content.filter((item) => item);
    });
    this.homePage = this.homePage.filter(
      (item) => item && item.content && item.content.length > 0
    );
  }

  getMenuTags(id: number, tableName: string): Promise<MenuTagModel[]> {
    return new Promise((resolve, reject) => {
      this.http
        .get(environment.baseURL + '/api' + '/menu_tag/' + tableName + '/' + id)
        .subscribe(
          (datas: MenuTagModel[]) => {
            resolve(datas);
          },
          (err) => {
            reject(err);
          }
        );
    });
  }

  deleteTagData(id: number) {
    return new Promise((resolve, reject) => {
      this.http
        .delete(environment.baseURL + '/api' + '/menu_tag/' + id)
        .subscribe(
          (datas: MenuTagModel) => {
            resolve(datas);
          },
          (err) => {
            reject(err);
          }
        );
    });
  }

  postTagData(data: MenuTagModel): Promise<MenuTagModel> {
    return new Promise((resolve, reject) => {
      this.http
        .post(environment.baseURL + '/api' + '/menu_tag', data)
        .subscribe(
          (datas: MenuTagModel) => {
            resolve(datas);
          },
          (err) => {
            reject(err);
          }
        );
    });
  }

  // environment.baseURL + '/v2/api' + '/bullet_list/' + catalog.menu_item_id + '?category_id='
  getCatalogBulletList(
    catalog: MenuItemCatalog
  ): Promise<MenuItemBulletList[]> {
    return new Promise((resolve, reject) => {
      this.http
        .get(
          environment.apiURL +
            '/bullet_list/' +
            catalog.menu_item_id +
            '?category_id=' +
            catalog.id
        )
        .subscribe(
          (datas: MenuItemBulletList[]) => {
            resolve(datas);
          },
          (err) => {
            reject(err);
          }
        );
    });
  }

  getLinkwithSub(subdomain: string): Promise<any> {
    return new Promise((resolve) => {
      const path = environment.baseURL + '/getHotelType/' + subdomain;

      this.http.get(path).subscribe(
        (resp: any) => {
          localStorage.setItem('basics', JSON.stringify(resp));
          localStorage.setItem('hotelId', JSON.stringify(resp.hotel_id));
          environment.hotelId = resp.hotel_id;
          resolve(resp);
        },
        (err) => {
          resolve(err);
        }
      );
    });
  }

  async getAllHotels(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http
        .get('https://api.icibot.net/v2/hotel_list')
        .subscribe({
          next: (resp) => {
            resolve(resp);
          },
          error: (err) => {
            const error =
              err.error && err.error.message ? err.error.message : err.message;
            this.toaster.toastMachine(error, 'danger', 3000);
            reject(err);
          },
        });
    });
  }
}

export interface HomePageItem {
  title: string;
  show_only_logged: boolean;
  content: HomePageItemContent[];
}

export interface HomePageItemContent {
  title: string;
  image: string;
  route: string;
  type: 'item' | 'section';
  section: MenuSection;
  item: MenuItem;
}
